import { formatViewingDuration } from './formatViewingDuration'

export const DASH = '—'

const FIGURE_NAMES = [
    'viewings',
    'viewingDuration',
    'barViewings',
    'popupViewings',
    'barViewingDuration',
    'popupViewingDuration',
    'viewers',
    'conversions',
    'converters',
    'averageViewingsPerUser',
    'averageViewingDurationPerUser',
]

const DURATION_FIGURES = new Set([
    'viewingDuration',
    'barViewingDuration',
    'popupViewingDuration',
    'averageViewingDurationPerUser',
])

const allDashes = () => Object.fromEntries(FIGURE_NAMES.map((name) => [name, DASH]))

// One decimal, and no trailing '.0' on a whole number: an average of exactly 4 reads as
// a count, not as a measurement carrying more precision than it has.
function formatAverage(value) {
    return String(Math.round(value * 10) / 10)
}

const textOr = (value) => (value == null || value === '' ? DASH : value)

const timeOr = (value) => (value == null ? DASH : value)

/**
 * An announcement nobody has been exposed to and never converted on has no statistics
 * rather than statistics of zero, so every figure reads as a dash. Once anything has
 * happened, a zero among the figures is a measured zero and reads as one.
 */
export const CONVERSION_OUTCOMES = ['success', 'pending', 'abandoned', 'error']

const countOf = (outcomes, outcome) => Number(
    (outcomes ?? []).find((one) => one.outcome === outcome)?.conversions ?? 0,
)

/**
 * One row per action, labelled by the name the action carries now: a conversion records
 * the action's key, so renaming one relabels its history without rewriting it.
 */
export function resolveActionStats(stats) {
    return (stats?.actions ?? []).map((action) => ({
        id: action.key,
        name: textOr(action.name),
        conversions: Number(action.conversions ?? 0),
        converters: Number(action.converters ?? 0),
        ...Object.fromEntries(CONVERSION_OUTCOMES.map((outcome) => (
            [outcome, countOf(action.outcomes, outcome)]
        ))),
    }))
}

export function resolveAnnouncementStats(stats) {
    const totals = stats?.totals
    const users = stats?.users ?? []
    const nothingHappened = totals == null
        || (Number(totals.viewings ?? 0) === 0 && Number(totals.conversions ?? 0) === 0)

    if (nothingHappened) {
        return { figures: allDashes(), rows: [] }
    }

    const figures = Object.fromEntries(FIGURE_NAMES.map((name) => {
        const value = totals[name]

        if (value == null) {
            return [name, DASH]
        }

        if (DURATION_FIGURES.has(name)) {
            return [name, formatViewingDuration(value)]
        }

        if (name.startsWith('average')) {
            return [name, formatAverage(Number(value))]
        }

        return [name, String(value)]
    }))

    const rows = users.map((user) => ({
        id: user.userId,
        firstName: textOr(user.firstName),
        lastName: textOr(user.lastName),
        username: textOr(user.username),
        email: textOr(user.email),
        barViewings: Number(user.barViewings ?? 0),
        popupViewings: Number(user.popupViewings ?? 0),
        barViewingDuration: formatViewingDuration(user.barViewingDuration),
        popupViewingDuration: formatViewingDuration(user.popupViewingDuration),
        conversions: Number(user.conversions ?? 0),
        lastImpressionAt: timeOr(user.lastImpressionAt),
        lastConversionAt: timeOr(user.lastConversionAt),
    }))

    return { figures, rows }
}
